/**
 * **実写映像との位置合わせのタイムライン**（Issue #642。`/duel-editor`）。
 *
 * Web でやるのは**演出の時刻を実写に合わせることだけ**（ユーザー判断 2026-10-01。汎用の編集ソフトは畳んだ）。
 * 並べるのは演出の帯だけで、**帯をドラッグするとその帯のイベント（アンカー）の時刻が動く**
 * （HUD・盤面もそこまで待つ）。並びが入れ替わる動かし方はコアが断るので、理由を出して元に戻す。
 *
 * | 操作 | 何が起きるか |
 * |---|---|
 * | 目盛・空いた所を押す | そこへ移動（`onSeek`） |
 * | 帯をドラッグ | その帯のイベントを動かす（`anchor/move`）。**同じ状況名の motion と effect は組で動く**（`glue.js`） |
 * | 帯をダブルクリック | そのイベントを元の時刻（答えた時刻）に戻す（`anchor/clear`。組ごと） |
 * | 取り消す / やり直す | 文書の履歴（`editor.undo` / `redo`） |
 * | 設定の印を押す・ドラッグ | その「設定の変化」を選ぶ・時刻を動かす（Issue #643。中身はページが持つ） |
 *
 * 動かしたイベントの帯は黄色の縁。**時計は持たない**（再生位置は `editor.transport.ms`）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { editableObjects } from '../duelEditor/timeline/timeline';
import { describeChange } from '../editorPorts/timingSettings';
import { clipLook } from './clipLook';
import { gluedClips } from './glue';
import {
  DEFAULT_ZOOM, LANE_H, formatClock, msToX, snapMs, stackRows, tickStepMs, xToMs, zoomBy,
} from './timelineGeometry';

const RULER_H = 24;
const SETTING_H = 26;

/**
 * 帯を動かす量 → コマンド。帯の時刻はアンカーの時刻そのものなので、アンカーを同じだけ動かす
 * @returns コマンド。動かせる帯でなければ null
 */
export function moveCommand(obj, deltaMs, anchors = [], keys = null) {
  // **くっついて動く組**（`glue.js`）の鍵をまとめて同じだけ動かす。無ければその帯の鍵だけ
  const list = (keys && keys.length ? keys : [obj?.source?.anchorKey]).filter((k) => k != null);
  const moves = list.map((key) => {
    const anchor = anchors.find((a) => a.key === key);
    return anchor ? { key, ms: Math.round(anchor.ms + deltaMs) } : null;
  }).filter(Boolean);
  if (!moves.length || !deltaMs) return null;
  return { type: 'anchor/move', moves };
}

/**
 * @param changes 設定の変化 `[{ id, ms, set }]`（無ければ設定のレーンを出さない）
 * @param selectedChange 選んでいる変化の id
 * @param onSelectChange 変化を押したとき（id）
 * @param onMoveChange 変化を動かしたとき（id, ms）
 */
export default function DuelTimeline({
  editor, onSeek, playing = false, height = 180, saveState = null,
  changes = null, selectedChange = null, onSelectChange = null, onMoveChange = null,
}) {
  const { timeline, transport } = editor;
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [drag, setDrag] = useState(null);   // { ids: Set, delta }
  const [flagDrag, setFlagDrag] = useState(null);   // { id, ms }
  const [message, setMessage] = useState(null);
  const areaRef = useRef(null);
  const flashTimer = useRef(null);

  const clips = editableObjects(timeline).filter((o) => o.source?.anchorKey != null);
  const stack = stackRows(clips);
  const touched = new Set((timeline.anchors ?? []).filter((a) => a.touched).map((a) => a.key));
  const durationMs = Math.max(10000, clips.reduce((m, o) => Math.max(m, o.endMs), 0) + 4000, transport.ms + 2000);
  const width = msToX(durationMs, zoom);

  const flash = (text) => {
    if (!text) return;
    setMessage(text);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setMessage(null), 4000);
  };
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const msAt = useCallback((clientX) => {
    const box = areaRef.current?.getBoundingClientRect();
    return Math.max(0, xToMs((clientX - (box?.left ?? 0)) + (areaRef.current?.scrollLeft ?? 0), zoom));
  }, [zoom]);

  // **再生中は再生位置が見える所まで送る**（右端の手前で先へめくる）
  useEffect(() => {
    const el = areaRef.current;
    if (!el || !playing) return;
    const x = msToX(transport.ms, zoom);
    if (x < el.scrollLeft || x > el.scrollLeft + el.clientWidth * 0.85) el.scrollLeft = Math.max(0, x - el.clientWidth * 0.15);
  }, [transport.ms, zoom, playing]);

  const startDrag = (e, obj) => {
    e.preventDefault();
    e.stopPropagation();
    // **くっついて動く組**（同じ状況名の motion と effect。Issue #644）はまとめて動かす
    const group = gluedClips(clips, obj);
    const ids = new Set(group.map((o) => o.id));
    const fromMs = msAt(e.clientX);
    const targets = [0, transport.ms, ...clips.filter((o) => !ids.has(o.id)).flatMap((o) => [o.startMs, o.endMs])];
    const deltaOf = (clientX) => Math.round(snapMs(obj.startMs + (msAt(clientX) - fromMs), targets, zoom) - obj.startMs);
    const onMove = (ev) => setDrag({ ids, delta: deltaOf(ev.clientX) });
    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDrag(null);
      const keys = [...new Set(group.map((o) => o.source.anchorKey))];
      const cmd = moveCommand(obj, deltaOf(ev.clientX), timeline.anchors ?? [], keys);
      if (!cmd) return;
      const res = editor.dispatch(cmd);
      if (res && !res.ok) flash(res.reason);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  /** 設定の印のドラッグ（押しただけなら選ぶ） */
  const startFlag = (e, change) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectChange?.(change.id);
    const fromX = e.clientX;
    const fromMs = msAt(e.clientX);
    const at = (clientX) => Math.max(0, Math.round(change.ms + (msAt(clientX) - fromMs)));
    const onMove = (ev) => setFlagDrag({ id: change.id, ms: at(ev.clientX) });
    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setFlagDrag(null);
      if (Math.abs(ev.clientX - fromX) >= 3) onMoveChange?.(change.id, at(ev.clientX));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const reset = (obj) => {
    // 組ごと元の時刻へ（1 手で取り消せるようにまとめて送る）
    const keys = [...new Set(gluedClips(clips, obj).map((o) => o.source?.anchorKey))].filter((k) => k != null && touched.has(k));
    if (!keys.length) return;
    const res = editor.dispatchAll(keys.map((key) => ({ type: 'anchor/clear', key })));
    if (res && !res.ok) flash(res.reason);
  };

  const step = tickStepMs(zoom);
  const ticks = [];
  for (let ms = 0; ms <= durationMs; ms += step) ticks.push(ms);
  const btn = { background: '#2a2e35', color: '#ddd', border: '1px solid #3a3f47', borderRadius: 4, padding: '1px 8px', cursor: 'pointer' };

  return (
    <div data-testid="duel-timeline" style={{ display: 'flex', flexDirection: 'column', height, background: '#15171b', color: '#ddd', fontSize: 12, borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderBottom: '1px solid #2a2e35', flexWrap: 'wrap' }}>
        <span style={{ opacity: 0.7 }}>位置合わせ</span>
        <button type="button" style={btn} onClick={() => setZoom((z) => zoomBy(z, -1))} aria-label="縮小">−</button>
        <button type="button" style={btn} onClick={() => setZoom((z) => zoomBy(z, 1))} aria-label="拡大">＋</button>
        <button type="button" style={btn} data-testid="timeline-undo" disabled={!editor.canUndo} onClick={() => editor.undo()}>取り消す</button>
        <button type="button" style={btn} data-testid="timeline-redo" disabled={!editor.canRedo} onClick={() => editor.redo()}>やり直す</button>
        {saveState ? <span data-testid="timeline-save" style={{ opacity: 0.75 }}>{saveState}</span> : null}
        {message ? <span data-testid="timeline-message" style={{ color: '#ffb4b4' }}>{message}</span> : null}
        <span style={{ marginLeft: 'auto', opacity: 0.6 }}>帯をドラッグでイベントの時刻を動かす・ダブルクリックで元に戻す</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="timeline-clock">{formatClock(transport.ms)}</span>
      </div>
      <div ref={areaRef} style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'auto', position: 'relative' }}>
        <div style={{ width, position: 'relative', minHeight: '100%' }}>
          <div
            data-testid="timeline-ruler"
            onPointerDown={(e) => onSeek?.(msAt(e.clientX))}
            style={{ height: RULER_H, position: 'sticky', top: 0, zIndex: 1, background: '#15171b', borderBottom: '1px solid #2a2e35', cursor: 'pointer', userSelect: 'none' }}
          >
            {ticks.map((ms) => (
              <span key={ms} style={{ position: 'absolute', left: msToX(ms, zoom) + 2, top: 4, color: '#7d8590' }}>{formatClock(ms)}</span>
            ))}
          </div>
          <div
            data-testid="timeline-lane"
            onPointerDown={(e) => { if (e.target === e.currentTarget) onSeek?.(msAt(e.clientX)); }}
            style={{ height: stack.count * LANE_H + 6, position: 'relative', background: '#191c21' }}
          >
            {clips.map((o) => {
              const d = drag?.ids?.has(o.id) ? drag.delta : 0;
              const moved = touched.has(o.source.anchorKey);
              const label = editor.registry.get(o.type)?.label ?? o.type;
              const look = clipLook(o.type);
              return (
                <div
                  key={o.id}
                  data-testid={`clip-${o.id}`}
                  data-touched={moved ? 'true' : 'false'}
                  data-role={look.role}
                  onPointerDown={(e) => startDrag(e, o)}
                  onDoubleClick={() => reset(o)}
                  title={`${look.role === 'motion' ? 'カードの動き' : '演出'} ${label} ${formatClock(o.startMs)} 〜 ${formatClock(o.endMs)}${moved ? '（動かした）' : ''}`}
                  style={{
                    position: 'absolute', left: msToX(o.startMs + d, zoom), top: (stack.rows.get(o.id) ?? 0) * LANE_H + 3,
                    height: LANE_H - 8, width: Math.max(6, msToX(o.endMs - o.startMs, zoom)),
                    background: look.background,
                    border: moved ? '2px solid #f0c040' : '1px solid rgba(0,0,0,0.5)', borderRadius: 4,
                    color: '#fff', padding: '0 6px', overflow: 'hidden', whiteSpace: 'nowrap', boxSizing: 'border-box',
                    display: 'flex', alignItems: 'center', cursor: 'grab',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                </div>
              );
            })}
          </div>
          {changes ? (
            <div
              data-testid="timeline-settings"
              onPointerDown={(e) => { if (e.target === e.currentTarget) { onSelectChange?.(null); onSeek?.(msAt(e.clientX)); } }}
              style={{ height: SETTING_H, position: 'relative', background: '#16191e', borderTop: '1px solid #2a2e35' }}
            >
              {changes.map((c) => {
                const ms = flagDrag?.id === c.id ? flagDrag.ms : c.ms;
                const picked = c.id === selectedChange;
                return (
                  <div
                    key={c.id}
                    data-testid={`setting-${c.id}`}
                    data-selected={picked ? 'true' : 'false'}
                    onPointerDown={(e) => startFlag(e, c)}
                    title={`${formatClock(ms)} ${describeChange(c.set)}`}
                    style={{
                      position: 'absolute', left: msToX(ms, zoom), top: 3, height: SETTING_H - 6, maxWidth: 220,
                      background: picked ? '#3d6a9a' : '#2c4a66', color: '#fff', borderLeft: '3px solid #8fc4ff',
                      padding: '0 6px', fontSize: 11, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'grab', boxSizing: 'border-box', borderRadius: '0 4px 4px 0',
                    }}
                  >
                    {describeChange(c.set)}
                  </div>
                );
              })}
            </div>
          ) : null}
          <div data-testid="playhead" style={{ position: 'absolute', left: msToX(transport.ms, zoom), top: 0, bottom: 0, width: 2, background: '#ff4d5a', pointerEvents: 'none', zIndex: 2 }} />
        </div>
      </div>
    </div>
  );
}
